import { Website } from '../../components/context/CreatorContext';
import { SocialDetails } from '../../components/context/SignupContext';
import userClient from '../../utils/apiClients/userClient';
import { setLoading, setProfile } from '../slices/profileSlice';
import { AppDispatch } from '../store';

export type ProfileResponse = {
  profile: {
    artist: {
      id: string;
      fullName: string;
      about: string;
      categories: Array<string>;
      website: Website;
      headshot: string;
      coverphoto: string;
      facebook: SocialDetails;
      instagram: SocialDetails;
      tiktok: SocialDetails;
      x?: SocialDetails;
      twitter?: SocialDetails;
    } | null;
    creator: {
      id: string;
      fullName: string;
      about: string;
      categories: Array<string>;
      website: Website;
      headshot: string;
      coverphoto: string;
      facebook: SocialDetails;
      instagram: SocialDetails;
      tiktok: SocialDetails;
      x?: SocialDetails;
      twitter?: SocialDetails;
    } | null;
  } | null;
};

const createProfileDetails = (profile: ProfileResponse['profile']['artist'] | ProfileResponse['profile']['creator'], accountName: string | undefined) => ({
  ...profile,
  ...(accountName && { entityName: accountName, accountName }),
  socialNetworks: {
    facebook: { handle: profile?.facebook.handle ?? '', followers: profile?.facebook?.followers ?? 0 },
    instagram: { handle: profile?.instagram.handle ?? '', followers: profile?.instagram?.followers ?? 0 },
    tiktok: { handle: profile?.tiktok.handle ?? '', followers: profile?.tiktok?.followers ?? 0 },
    x: { handle: profile?.x?.handle ?? profile?.twitter?.handle ?? '', followers: profile?.x?.followers ?? profile?.twitter?.followers ?? 0 },
    twitter: { handle: profile?.twitter?.handle ?? profile?.x?.handle ?? '', followers: profile?.twitter?.followers ?? profile?.x?.followers ?? 0 }
  }
});

export const setProfileDetails = () => async (dispatch: AppDispatch) => {
  try {
    dispatch(setLoading(true));
    const { data } = await userClient.get('users/me/profiles');
    const artistProfile = createProfileDetails(data.artist, data?.artist?.accountName ?? '');
    const creatorProfile = createProfileDetails(data.creator, data?.creator?.accountName ?? '');
    const influencerProfile = createProfileDetails(data.influencer, data?.influencer?.accountName ?? '');
    dispatch(
      setProfile({
        ...data,
        artist: { 
          ...artistProfile, 
          accountName: data?.artist?.entityName,
          // Always use coverImage from API response (prioritize coverImage over coverphoto)
          coverImage: (data?.artist as any)?.coverImage || data?.artist?.coverphoto || '',
          coverphoto: (data?.artist as any)?.coverImage || data?.artist?.coverphoto || ''
        },
        creator: { 
          ...creatorProfile, 
          accountName: data?.creator?.entityName,
          // Always use coverImage from API response (prioritize coverImage over coverphoto)
          coverImage: data?.creator?.coverImage || '',
          coverphoto: data?.creator?.coverImage || ''
        },
        influencer: {
          ...influencerProfile,
          accountName: data?.influencer?.companyName,
          licenses: [{ type: 'personal', price: 0, ...data?.influencer?.license }],
          license: { type: 'personal', price: 0, ...data?.influencer?.license },
          artstyleProfileId: data?.influencer?.artstyleProfileId,
          // Always use coverImage from API response (prioritize coverImage over coverphoto)
          coverImage: data?.influencer?.coverImage || '',
          coverphoto: data?.influencer?.coverImage || ''
        }
      })
    );
  } catch (error) {
    console.error('Error fetching user profile:', error);
  } finally {
    dispatch(setLoading(false));
  }
};
